<template>
  <transition name="csw-fade">
    <div
      v-if="visible"
      class="csw-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="csw-title"
      @click.self="requestClose"
      @keydown.esc.stop="requestClose"
    >
      <div ref="shell" class="csw-shell" tabindex="-1" @keydown.tab="trapFocus">
        <header class="csw-header">
          <button class="csw-close" type="button" @click="requestClose" aria-label="Close">×</button>
          <span class="csw-eyebrow">Whole-company signup</span>
          <h2 id="csw-title">{{ stepHeading }}</h2>
          <ol class="csw-progress" aria-label="Progress">
            <li :class="progressClass(1)">
              <span class="csw-progress-num">1</span>
              <span class="csw-progress-label">Company</span>
            </li>
            <li :class="progressClass(2)">
              <span class="csw-progress-num">2</span>
              <span class="csw-progress-label">Plan</span>
            </li>
            <li :class="progressClass(3)">
              <span class="csw-progress-num">3</span>
              <span class="csw-progress-label">Pay</span>
            </li>
          </ol>
        </header>

        <!-- v-show, NOT v-if. Stripe Elements iframe in step 3 must stay
             mounted across Back/Next or tokenization silently breaks. -->

        <!-- ───────── Step 1: Company info + admin info ───────── -->
        <section v-show="step === 1" class="csw-step csw-step-1">
          <p class="csw-step-intro">
            Tell us about your company and the person who'll be the admin.
            Takes about 30 seconds.
          </p>
          <div class="csw-grid">
            <label class="csw-field">
              <span>Company name <span class="csw-req">*</span></span>
              <input
                v-model="buyer.company_name"
                type="text"
                autocomplete="organization"
                placeholder="Acme Hospitality LLC"
                @blur="touch('company_name')"
              />
            </label>
            <label class="csw-field">
              <span>Phone <span class="csw-req">*</span></span>
              <input
                v-model="buyer.phone"
                type="tel"
                autocomplete="tel"
                placeholder="(305) 555-0199"
                @blur="touch('phone')"
              />
              <small v-if="touched.phone && !validPhone" class="csw-err">
                Use at least 10 digits.
              </small>
            </label>
            <label class="csw-field">
              <span>First name <span class="csw-req">*</span></span>
              <input
                v-model="buyer.first_name"
                type="text"
                autocomplete="given-name"
                @blur="touch('first_name')"
              />
            </label>
            <label class="csw-field">
              <span>Last name <span class="csw-req">*</span></span>
              <input
                v-model="buyer.last_name"
                type="text"
                autocomplete="family-name"
                @blur="touch('last_name')"
              />
            </label>
            <label class="csw-field csw-field--wide">
              <span>Work email <span class="csw-req">*</span></span>
              <input
                v-model="buyer.email"
                type="email"
                autocomplete="email"
                placeholder="you@company.com"
                @blur="touch('email')"
              />
              <small v-if="touched.email && !validEmail" class="csw-err">
                Use a valid email address.
              </small>
            </label>
            <label class="csw-field">
              <span>Password <span class="csw-req">*</span></span>
              <input
                v-model="buyer.password"
                type="password"
                autocomplete="new-password"
                @blur="touch('password')"
              />
              <small v-if="touched.password && !validPassword" class="csw-err">
                Use 8–72 characters.
              </small>
            </label>
            <label class="csw-field">
              <span>Confirm password <span class="csw-req">*</span></span>
              <input
                v-model="buyer.password_confirm"
                type="password"
                autocomplete="new-password"
                @blur="touch('password_confirm')"
              />
              <small v-if="touched.password_confirm && !passwordsMatch" class="csw-err">
                Passwords don't match.
              </small>
            </label>
          </div>
        </section>

        <!-- ───────── Step 2: Plan + sliders + cycle ───────── -->
        <section v-show="step === 2" class="csw-step csw-step-2">
          <p class="csw-step-intro">
            Pick the plan that fits. You can adjust seats, locations, and
            billing cycle below — we'll show the running total.
          </p>

          <div class="csw-plans">
            <button
              v-for="plan in resolvedCompanyPlans"
              :key="plan.key"
              type="button"
              :class="[
                'csw-plan-card',
                { 'csw-plan-card--selected': companyConfig.plan === plan.key },
                { 'csw-plan-card--popular': plan.popular },
              ]"
              @click="selectPlan(plan.key)"
            >
              <span v-if="plan.popular" class="csw-plan-badge">Most popular</span>
              <span class="csw-plan-name">{{ plan.name }}</span>
              <span class="csw-plan-tagline">{{ plan.tagline || '' }}</span>
              <span class="csw-plan-price">
                ${{ formatPrice(planRate(plan)) }}<small>/employee/mo</small>
              </span>
              <ul class="csw-plan-bullets">
                <li v-for="(b, i) in plan.bullets || []" :key="i">{{ b }}</li>
              </ul>
            </button>
          </div>

          <div class="csw-config">
            <div class="csw-config-row">
              <label class="csw-config-label">
                <span>Total employees</span>
                <input
                  type="number"
                  min="1"
                  max="100000"
                  step="1"
                  :value="companyConfig.total_users"
                  @input="onUsersInput($event.target.value)"
                />
              </label>
              <label class="csw-config-label">
                <span>Locations</span>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  step="1"
                  :value="companyConfig.locations"
                  @input="onLocationsInput($event.target.value)"
                />
              </label>
            </div>

            <div class="csw-cycle" role="radiogroup" aria-label="Billing cycle">
              <button
                type="button"
                :class="['csw-cycle-opt', { 'csw-cycle-opt--active': companyConfig.billing_cycle === 'monthly' }]"
                @click="setBillingCycle('monthly')"
              >Monthly</button>
              <button
                type="button"
                :class="['csw-cycle-opt', { 'csw-cycle-opt--active': companyConfig.billing_cycle === 'annual' }]"
                @click="setBillingCycle('annual')"
              >Annual <span class="csw-cycle-save">save 10%</span></button>
            </div>
          </div>

          <div class="csw-summary">
            <div class="csw-summary-row">
              <span>Subtotal</span>
              <strong>${{ formatPrice(displayedSubtotal) }}</strong>
            </div>
            <div v-if="displayedDiscount > 0" class="csw-summary-row">
              <span>Annual savings (10%)</span>
              <strong class="csw-summary-discount">−${{ formatPrice(displayedDiscount) }}</strong>
            </div>
            <div class="csw-summary-row csw-summary-row--total">
              <span>Total {{ companyConfig.billing_cycle === 'annual' ? '(annual)' : '(monthly)' }}</span>
              <strong>${{ formatPrice(displayedTotal) }}</strong>
            </div>
          </div>
        </section>

        <!-- ───────── Step 3: Stripe Elements + Pay ───────── -->
        <section v-show="step === 3" class="csw-step csw-step-3">
          <p class="csw-step-intro">
            Review and pay. Card info is handled by Stripe — we never see it.
          </p>

          <div class="csw-recap">
            <div class="csw-recap-row">
              <span>{{ selectedPlan ? selectedPlan.name : 'Plan' }} · {{ companyConfig.total_users }} employees · {{ companyConfig.locations }} location{{ companyConfig.locations === 1 ? '' : 's' }}</span>
              <strong>${{ formatPrice(displayedTotal) }}/{{ companyConfig.billing_cycle === 'annual' ? 'yr' : 'mo' }}</strong>
            </div>
            <button type="button" class="csw-recap-edit" @click="step = 2">Edit plan</button>
          </div>

          <div class="csw-card-field">
            <label class="csw-card-label">Card details</label>
            <stripe-element-card
              ref="stripeCard"
              :pk="stripePublicKey"
              :hidePostalCode="true"
              @token="onStripeToken"
              @error="onStripeError"
            />
          </div>

          <div v-if="paymentError" class="csw-payment-error" role="alert">
            {{ paymentError }}
          </div>

          <button
            type="button"
            class="csw-btn csw-btn--primary csw-btn--pay csw-btn--full"
            :disabled="!canPay || checkoutLoading"
            @click="submitPayment"
          >
            <span v-if="checkoutLoading">Processing…</span>
            <span v-else>Pay ${{ formatPrice(displayedTotal) }}/{{ companyConfig.billing_cycle === 'annual' ? 'yr' : 'mo' }} →</span>
          </button>

          <p class="csw-trial-link" :class="{ 'csw-trial-link--disabled': !step1Complete }">
            Just want to try it free?
            <a
              href="#"
              class="csw-trial-anchor"
              :class="{ 'csw-trial-anchor--disabled': !step1Complete }"
              :aria-disabled="!step1Complete ? 'true' : 'false'"
              @click.prevent="startFreeTrial"
            >
              Start a 7-day trial — no card required →
            </a>
          </p>
          <div v-if="trialSignupError" class="csw-payment-error" role="alert">
            {{ trialSignupError }}
          </div>

          <p class="csw-trust">Secure · Stripe · 7-day trial · No setup fees</p>
        </section>

        <footer class="csw-footer">
          <button v-if="step > 1" type="button" class="csw-btn csw-btn--ghost" @click="goBack">← Back</button>
          <button
            v-if="step < 3"
            type="button"
            class="csw-btn csw-btn--primary"
            :disabled="!canAdvance"
            @click="goNext"
          >
            Continue →
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import { StripeElementCard } from '@vue-stripe/vue-stripe';

/**
 * CompanySignupWizard — three-step signup flow for whole-company buyers.
 *
 * Self-contained: owns its own buyer/companyConfig/payment state, talks
 * directly to /new-checkout/cart/update-company and /checkout/intent.
 * Emits paid/trialed events for the parent to handle redirects.
 *
 * Critical technique: every step uses v-show, not v-if. The
 * <stripe-element-card> in Step 3 mounts an iframe; unmounting/remounting
 * between Back/Next causes Stripe.js to leak listeners and silently fail
 * tokenization.
 */
export default {
  name: 'CompanySignupWizard',
  components: { StripeElementCard },

  props: {
    visible: { type: Boolean, default: false },
    cart: { type: Object, default: () => ({}) },
    sessionUuid: { type: String, default: '' },
    catalog: { type: Array, default: () => [] },
    planIncludedIds: { type: Object, default: () => ({}) },
    companyPlans: { type: Array, default: () => [] },
    altOptions: { type: Array, default: () => [] },
    apiBase: { type: String, default: '' },
    stripePublicKey: { type: String, default: '' },
    initialStep: { type: Number, default: 1 },
    initialPlan: { type: String, default: null },
  },

  data() {
    return {
      step: this.initialStep || 1,
      buyer: {
        first_name: '',
        last_name: '',
        company_name: '',
        email: '',
        phone: '',
        password: '',
        password_confirm: '',
      },
      touched: {
        first_name: false,
        last_name: false,
        company_name: false,
        email: false,
        phone: false,
        password: false,
        password_confirm: false,
      },
      companyConfig: {
        plan: this.initialPlan || 'sizzling',
        billing_cycle: 'monthly',
        total_users: 25,
        locations: 1,
      },
      companyDebounce: null,
      checkoutLoading: false,
      paymentError: '',
      trialSignupLoading: false,
      trialSignupError: '',
    };
  },

  computed: {
    /**
     * Default plan grid when the parent hasn't supplied one. Owner-directed
     * shape: 3 cards (HOT/SIZZLING/SCORCHING). The wizard works either way —
     * if companyPlans prop has data, that wins; otherwise the fallback is
     * a safe minimal config so the wizard renders cleanly behind the flag.
     */
    resolvedCompanyPlans() {
      if (Array.isArray(this.companyPlans) && this.companyPlans.length) {
        return this.companyPlans;
      }
      return [
        {
          key: 'hot',
          name: 'HOT',
          tagline: 'Compliance essentials',
          basePerEmployee: 4.5,
          bullets: ['Food handler training', 'Required posters', 'OSHA-10'],
        },
        {
          key: 'sizzling',
          name: 'SIZZLING',
          tagline: 'Most popular',
          basePerEmployee: 7,
          popular: true,
          bullets: [
            'Everything in HOT',
            'Sexual harassment training',
            'Compliance dashboard',
          ],
        },
        {
          key: 'scorching',
          name: 'SCORCHING',
          tagline: 'Full library',
          basePerEmployee: 12,
          bullets: ['Everything in SIZZLING', 'Full course library', 'Custom courses'],
        },
      ];
    },

    selectedPlan() {
      const k = this.companyConfig.plan;
      return this.resolvedCompanyPlans.find((p) => p.key === k) || null;
    },

    stepHeading() {
      if (this.step === 1) return 'Tell us about your company';
      if (this.step === 2) return 'Pick your plan';
      return 'Review and pay';
    },

    validEmail() {
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((this.buyer.email || '').trim());
    },
    validPhone() {
      const digits = (this.buyer.phone || '').replace(/\D/g, '');
      return digits.length >= 10 && digits.length <= 15;
    },
    validPassword() {
      const len = (this.buyer.password || '').length;
      return len >= 8 && len <= 72;
    },
    passwordsMatch() {
      return this.buyer.password === this.buyer.password_confirm;
    },
    step1Complete() {
      return this.validEmail
        && this.validPhone
        && this.validPassword
        && this.passwordsMatch
        && (this.buyer.first_name || '').trim().length > 0
        && (this.buyer.last_name || '').trim().length > 0
        && (this.buyer.company_name || '').trim().length > 0;
    },
    step2Complete() {
      return !!this.companyConfig.plan && this.companyConfig.total_users > 0;
    },
    canAdvance() {
      if (this.step === 1) return this.step1Complete;
      if (this.step === 2) return this.step2Complete;
      return false;
    },
    canPay() {
      return this.step === 3
        && this.step1Complete
        && this.step2Complete
        && this.displayedTotal > 0
        && !this.checkoutLoading
        && !this.trialSignupLoading;
    },

    /** Monthly cost from plan rate × employees + $19/location. */
    monthlyTotal() {
      const plan = this.selectedPlan;
      if (!plan) return 0;
      const rate = this.planRate(plan);
      const seats = Math.max(1, Number(this.companyConfig.total_users) || 0);
      const locations = Math.max(1, Number(this.companyConfig.locations) || 0);
      const seatCost = rate * seats;
      const locationCost = (locations - 1) * 19;
      return Math.round((seatCost + locationCost) * 100) / 100;
    },
    displayedSubtotal() {
      if (this.companyConfig.billing_cycle === 'annual') {
        return Math.round(this.monthlyTotal * 12 * 100) / 100;
      }
      return this.monthlyTotal;
    },
    displayedDiscount() {
      if (this.companyConfig.billing_cycle !== 'annual') return 0;
      return Math.round(this.monthlyTotal * 12 * 0.10 * 100) / 100;
    },
    displayedTotal() {
      return Math.round((this.displayedSubtotal - this.displayedDiscount) * 100) / 100;
    },
  },

  watch: {
    visible(now) {
      if (now) {
        this.step = this.initialStep || 1;
        if (this.initialPlan) this.companyConfig.plan = this.initialPlan;
        this.$nextTick(() => {
          if (this.$refs.shell) this.$refs.shell.focus();
        });
        this.$emit('stepChanged', this.step);
      } else {
        // Always scrub plaintext password on close.
        this.buyer.password = '';
        this.buyer.password_confirm = '';
        this.paymentError = '';
        this.trialSignupError = '';
      }
    },
    initialStep(now) {
      if (this.visible) this.step = now;
    },
    initialPlan(now) {
      if (this.visible && now) this.companyConfig.plan = now;
    },
    'companyConfig.plan'() { this.queueCompanySync(); },
    'companyConfig.billing_cycle'() { this.queueCompanySync(); },
    'companyConfig.total_users'() { this.queueCompanySync(); },
    'companyConfig.locations'() { this.queueCompanySync(); },
  },

  methods: {
    progressClass(n) {
      return {
        'csw-progress-step': true,
        'csw-progress-step--active': this.step >= n,
        'csw-progress-step--current': this.step === n,
      };
    },

    touch(field) { this.touched[field] = true; },

    selectPlan(key) {
      this.companyConfig.plan = key;
    },

    setBillingCycle(cycle) {
      this.companyConfig.billing_cycle = cycle === 'annual' ? 'annual' : 'monthly';
    },

    onUsersInput(value) {
      const n = Math.max(1, Math.min(100000, parseInt(value, 10) || 1));
      this.companyConfig.total_users = n;
    },
    onLocationsInput(value) {
      const n = Math.max(1, Math.min(10000, parseInt(value, 10) || 1));
      this.companyConfig.locations = n;
    },

    /**
     * Push current companyConfig to the server cart so /checkout/intent
     * sees a server-side total that matches the wizard preview. Debounced
     * 450ms so slider drags don't flood the endpoint.
     */
    queueCompanySync({ immediate = false } = {}) {
      if (!this.sessionUuid || !this.apiBase) return Promise.resolve();
      if (this.companyDebounce) clearTimeout(this.companyDebounce);
      const fire = async () => {
        try {
          await axios.post(`${this.apiBase}/new-checkout/cart/update-company`, {
            session_uuid: this.sessionUuid,
            account_type: 'company',
            plan: this.companyConfig.plan,
            billing_cycle: this.companyConfig.billing_cycle,
            total_users: this.companyConfig.total_users,
            locations: this.companyConfig.locations,
          });
        } catch (e) { /* best-effort sync; Pay-time call is the source of truth */ }
      };
      if (immediate) return fire();
      return new Promise((resolve) => {
        this.companyDebounce = setTimeout(async () => {
          await fire();
          resolve();
        }, 450);
      });
    },

    goNext() {
      if (this.step >= 3) return;
      if (!this.canAdvance) return;
      // Mark all current-step fields touched so missed fields can show
      // their inline error states if the buyer comes back to this step.
      if (this.step === 1) {
        Object.keys(this.touched).forEach((k) => { this.touched[k] = true; });
      }
      this.step += 1;
      this.$emit('stepChanged', this.step);
      // On entering Step 3, push the latest config server-side so the
      // intent is computed against the correct total.
      if (this.step === 3) {
        this.queueCompanySync({ immediate: true });
      }
    },
    goBack() {
      if (this.step <= 1) return;
      this.step -= 1;
      this.$emit('stepChanged', this.step);
    },

    requestClose() {
      this.$emit('update:visible', false);
    },

    trapFocus(event) {
      const shell = this.$refs.shell;
      if (!shell) return;
      const focusable = shell.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    formatPrice(n) {
      const v = Number(n) || 0;
      return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // PricingPage passes plan objects with `per_user_rate`; the wizard's
    // built-in fallback uses `basePerEmployee`. Read either so the price
    // never silently renders as $0 if the parent shape changes.
    planRate(plan) {
      if (!plan) return 0;
      return Number(plan.basePerEmployee || plan.per_user_rate || 0);
    },

    /**
     * Pay button. Mirror PricingPage's startCheckout: sync cart, then
     * tokenize. The @token handler takes it from there.
     */
    async submitPayment() {
      if (!this.canPay) return;
      this.paymentError = '';
      this.checkoutLoading = true;
      try {
        await this.queueCompanySync({ immediate: true });
        const card = this.$refs.stripeCard;
        if (!card || typeof card.submit !== 'function') {
          this.paymentError = 'Card form is not ready — reload and try again.';
          this.checkoutLoading = false;
          return;
        }
        card.submit();
      } catch (e) {
        this.paymentError = 'Checkout failed to start. Please try again.';
        this.checkoutLoading = false;
      }
    },

    /** vue-stripe @token. POST /checkout/intent and let the server confirm. */
    async onStripeToken(token) {
      this.paymentError = '';
      try {
        const tokenId = token && token.id;
        if (!tokenId) {
          this.paymentError = 'Could not read card token — please re-enter your card.';
          this.checkoutLoading = false;
          return;
        }
        const { data } = await axios.post(`${this.apiBase}/new-checkout/checkout/intent`, {
          session_uuid: this.sessionUuid,
          email: this.buyer.email.trim(),
          first_name: (this.buyer.first_name || '').trim(),
          last_name: (this.buyer.last_name || '').trim(),
          company_name: (this.buyer.company_name || '').trim() || null,
          phone: (this.buyer.phone || '').trim() || null,
          password: this.buyer.password,
          payment_token: tokenId,
        });
        const intent = (data && data.intent) || {};
        const clearPassword = () => {
          this.buyer.password = '';
          this.buyer.password_confirm = '';
        };
        if (intent.status === 'succeeded' && intent.intent_id) {
          clearPassword();
          this.$emit('paid', { intent_id: intent.intent_id, email: this.buyer.email.trim() });
          return;
        }
        if (intent.status === 'requires_action' && intent.client_secret) {
          if (!window.Stripe) {
            this.paymentError = 'Card authentication was required but Stripe.js did not load.';
            this.checkoutLoading = false;
            return;
          }
          const stripe = window.Stripe(this.stripePublicKey);
          const result = await stripe.handleNextAction({ clientSecret: intent.client_secret });
          if (result.error) {
            this.paymentError = result.error.message || 'Card authentication failed.';
            this.checkoutLoading = false;
            return;
          }
          const pi = result.paymentIntent;
          if (pi && pi.status === 'succeeded') {
            clearPassword();
            this.$emit('paid', { intent_id: pi.id, email: this.buyer.email.trim() });
            return;
          }
          this.paymentError = 'Payment could not be completed. Please try a different card.';
          this.checkoutLoading = false;
          return;
        }
        if (intent.mock) {
          this.paymentError = intent.error || 'Stripe is not configured on this environment.';
          this.checkoutLoading = false;
          return;
        }
        if (intent.status === 'error' || intent.error) {
          this.paymentError = intent.error || 'Payment failed.';
          this.checkoutLoading = false;
          return;
        }
        this.paymentError = 'Unexpected response from payment server.';
        this.checkoutLoading = false;
      } catch (e) {
        this.paymentError = e.response?.data?.error
          || e.response?.data?.message
          || 'Payment request failed.';
        this.checkoutLoading = false;
      }
    },
    onStripeError(err) {
      this.paymentError = (err && err.message) || 'Please check your card details and try again.';
      this.checkoutLoading = false;
    },

    /**
     * Returns a one-line description of the first step-1 field that's
     * blocking the trial submit, or an empty string when step1Complete
     * is true. Order matches the visual field order on step 1 so the
     * buyer scrolls up to the right field.
     */
    firstStep1Gap() {
      if (!(this.buyer.first_name || '').trim()) return 'Add your first name on step 1.';
      if (!(this.buyer.last_name || '').trim()) return 'Add your last name on step 1.';
      if (!(this.buyer.company_name || '').trim()) return 'Add your company name on step 1.';
      if (!this.validEmail) return 'Enter a valid email on step 1.';
      if (!this.validPhone) return 'Enter a valid phone number on step 1 (10 digits).';
      if (!this.validPassword) return 'Set a password on step 1 (8+ characters).';
      if (!this.passwordsMatch) return 'Your passwords don\'t match — fix on step 1.';
      return '';
    },

    /**
     * Free-trial alt path. POST signup-trial with the same fields the
     * paid path uses, redirect on success.
     *
     * Owner UAT 2026-05-02: when step1Complete is false the prior code
     * silently early-returned with no UI feedback — buyer tapped the
     * trial link and "nothing happened." Now we surface an inline error
     * naming what's missing, and the link itself renders a disabled
     * state via :class binding so the buyer can see it's not clickable.
     */
    async startFreeTrial() {
      if (this.trialSignupLoading) return;
      if (!this.step1Complete) {
        this.trialSignupError = this.firstStep1Gap()
          || 'Please complete the personal info on step 1 first.';
        return;
      }
      this.trialSignupError = '';
      this.trialSignupLoading = true;
      try {
        const { data } = await axios.post(`${this.apiBase}/new-checkout/checkout/signup-trial`, {
          email: this.buyer.email.trim(),
          first_name: (this.buyer.first_name || '').trim(),
          last_name: (this.buyer.last_name || '').trim(),
          company_name: (this.buyer.company_name || '').trim(),
          phone: (this.buyer.phone || '').trim(),
          password: this.buyer.password,
          plan: this.companyConfig.plan,
          total_users: this.companyConfig.total_users,
          locations: this.companyConfig.locations,
        });
        if (data && data.status === 'ok') {
          this.buyer.password = '';
          this.buyer.password_confirm = '';
          this.$emit('trialed', data);
          return;
        }
        this.trialSignupError = (data && data.message) || 'Could not start your trial.';
      } catch (e) {
        this.trialSignupError = e.response?.data?.message
          || e.response?.data?.errors?.email?.[0]
          || 'Could not start your trial.';
      } finally {
        this.trialSignupLoading = false;
      }
    },
  },

  beforeDestroy() {
    if (this.companyDebounce) clearTimeout(this.companyDebounce);
  },
};
</script>

<style lang="scss" scoped>
$csw-brand: #FE672C;
$csw-brand-dark: #C24A14;
$csw-line: #e2e6ea;
$csw-text: #1f2937;
$csw-muted: #6b7280;
$csw-radius: 10px;
$csw-err: #c0392b;

.csw-fade-enter-active, .csw-fade-leave-active { transition: opacity 0.16s ease; }
.csw-fade-enter, .csw-fade-leave-to { opacity: 0; }

.csw-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9500;
  padding: 16px;
  @media (max-width: 767px) { padding: 0; }
}

.csw-shell {
  background: #fff;
  border-radius: $csw-radius;
  width: 100%;
  max-width: 720px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 28px 64px rgba(15, 23, 42, 0.32);
  outline: none;
  overflow: hidden;
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  color: $csw-text;
  @media (max-width: 767px) {
    max-width: 100%;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
}

.csw-header {
  position: relative;
  padding: 22px 22px 14px;
  border-bottom: 1px solid $csw-line;
  .csw-close {
    position: absolute;
    top: 10px; right: 10px;
    border: 0; background: transparent;
    font-size: 26px; line-height: 1;
    color: $csw-muted; cursor: pointer;
    padding: 4px 10px; border-radius: 8px;
    &:hover { background: rgba(15, 23, 42, 0.06); color: $csw-text; }
    &:focus-visible { outline: 2px solid $csw-brand; outline-offset: 2px; }
  }
  .csw-eyebrow {
    display: inline-block;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-size: 11px;
    font-weight: 700;
    color: $csw-brand-dark;
    margin-bottom: 6px;
  }
  h2 { font-size: 20px; font-weight: 700; margin: 0 0 14px; line-height: 1.25; }
}

.csw-progress {
  display: flex; list-style: none; margin: 0; padding: 0; gap: 8px;
}
.csw-progress-step {
  flex: 1 1 0;
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; font-weight: 600; color: $csw-muted;
  .csw-progress-num {
    width: 26px; height: 26px;
    border-radius: 50%; background: #f0f2f5; color: $csw-muted;
    display: inline-flex; align-items: center; justify-content: center;
    font-weight: 700; flex-shrink: 0;
    transition: background 0.16s, color 0.16s;
  }
  &--active { color: $csw-text;
    .csw-progress-num { background: rgba($csw-brand, 0.16); color: $csw-brand-dark; }
  }
  &--current .csw-progress-num { background: $csw-brand; color: #fff; }
  @media (max-width: 479px) { .csw-progress-label { display: none; } }
}

.csw-step {
  flex: 1 1 auto; overflow-y: auto;
  padding: 22px; font-size: 15px; line-height: 1.55;
}
.csw-step-intro { color: $csw-muted; margin: 0 0 18px; font-size: 14px; }

.csw-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  @media (max-width: 539px) { grid-template-columns: 1fr; }
}
.csw-field {
  display: flex; flex-direction: column; gap: 4px;
  font-size: 13px; font-weight: 600; color: $csw-text;
  &--wide { grid-column: 1 / -1; }
  .csw-req { color: $csw-brand-dark; }
  input {
    appearance: none;
    border: 1px solid $csw-line;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    font-weight: 400;
    color: $csw-text;
    background: #fff;
    min-height: 42px;
    &:focus { outline: none; border-color: $csw-brand; box-shadow: 0 0 0 3px rgba($csw-brand, 0.18); }
  }
  .csw-err { color: $csw-err; font-size: 12px; font-weight: 500; margin-top: 2px; }
}

.csw-plans {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 22px;
  @media (max-width: 639px) { grid-template-columns: 1fr; }
}
.csw-plan-card {
  position: relative;
  text-align: left;
  appearance: none;
  border: 2px solid $csw-line;
  background: #fff;
  border-radius: 10px;
  padding: 16px 14px;
  cursor: pointer;
  display: flex; flex-direction: column;
  transition: border-color 0.16s, box-shadow 0.16s;
  &:hover { border-color: rgba($csw-brand, 0.45); }
  &:focus-visible { outline: 2px solid $csw-brand; outline-offset: 2px; }
  &--selected { border-color: $csw-brand; box-shadow: 0 0 0 4px rgba($csw-brand, 0.12); }
  &--popular { background: linear-gradient(180deg, rgba($csw-brand, 0.05) 0%, #fff 80%); }
  .csw-plan-badge {
    position: absolute; top: -10px; right: 12px;
    background: $csw-brand; color: #fff;
    font-size: 10px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.4px;
    padding: 3px 8px; border-radius: 100px;
  }
  .csw-plan-name { font-size: 14px; font-weight: 800; color: $csw-text; }
  .csw-plan-tagline { font-size: 12px; color: $csw-muted; margin-bottom: 8px; }
  .csw-plan-price {
    font-size: 22px; font-weight: 800; color: $csw-text;
    margin: 6px 0 10px;
    small { font-size: 11px; font-weight: 500; color: $csw-muted; margin-left: 2px; }
  }
  .csw-plan-bullets {
    list-style: none; padding: 0; margin: 0;
    li {
      font-size: 12px; color: $csw-text; padding: 3px 0 3px 16px;
      position: relative;
      &::before { content: "✓"; color: $csw-brand-dark; position: absolute; left: 0; font-weight: 700; }
    }
  }
}

.csw-config { margin-bottom: 18px; }
.csw-config-row {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;
  @media (max-width: 479px) { grid-template-columns: 1fr; }
}
.csw-config-label {
  display: flex; flex-direction: column; gap: 4px;
  font-size: 13px; font-weight: 600;
  input {
    appearance: none;
    border: 1px solid $csw-line;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    min-height: 42px;
    &:focus { outline: none; border-color: $csw-brand; box-shadow: 0 0 0 3px rgba($csw-brand, 0.18); }
  }
}
.csw-cycle {
  display: inline-flex; gap: 0;
  border: 1px solid $csw-line; border-radius: 100px; overflow: hidden;
}
.csw-cycle-opt {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 8px 16px;
  font-size: 13px; font-weight: 600; color: $csw-muted;
  cursor: pointer;
  transition: background 0.16s, color 0.16s;
  .csw-cycle-save { font-size: 11px; font-weight: 600; color: $csw-brand-dark; margin-left: 4px; }
  &--active { background: $csw-brand; color: #fff;
    .csw-cycle-save { color: #fff; opacity: 0.9; }
  }
}

.csw-summary {
  background: #f7f8fa;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex; flex-direction: column; gap: 6px;
}
.csw-summary-row {
  display: flex; justify-content: space-between; font-size: 14px;
  span { color: $csw-muted; }
  strong { color: $csw-text; font-weight: 700; }
  &--total { border-top: 1px solid $csw-line; padding-top: 8px; margin-top: 4px;
    span, strong { font-size: 16px; }
  }
}
.csw-summary-discount { color: $csw-brand-dark; }

.csw-recap {
  display: flex; align-items: center; justify-content: space-between;
  background: #f7f8fa; border-radius: 10px; padding: 12px 14px;
  margin-bottom: 16px; font-size: 14px;
  .csw-recap-row { flex: 1; display: flex; flex-direction: column;
    span { color: $csw-muted; font-size: 12px; }
  }
  .csw-recap-edit {
    appearance: none; border: 0; background: transparent;
    color: $csw-brand-dark; font-size: 12px; font-weight: 700;
    cursor: pointer; padding: 4px 8px;
    &:hover { text-decoration: underline; }
  }
}

.csw-card-field { margin-bottom: 12px; }
.csw-card-label { font-size: 13px; font-weight: 600; color: $csw-text; display: block; margin-bottom: 6px; }
.csw-payment-error {
  background: #fff0ec;
  border: 1px solid rgba($csw-err, 0.35);
  color: $csw-err;
  font-size: 13px;
  font-weight: 500;
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}
.csw-trial-link {
  font-size: 13px; color: $csw-muted;
  text-align: center; margin: 14px 0 4px;
  .csw-trial-anchor { color: $csw-brand-dark; font-weight: 700; }
  // When step 1 isn't complete the trial flow can't fire — render the
  // link visibly disabled so the buyer sees the gate before tapping
  // (Owner UAT 2026-05-02 — silent no-op was reported as "trial
  // wasn't working"). Click handler still fires so we can surface the
  // specific missing-field message below.
  &--disabled .csw-trial-anchor--disabled {
    color: $csw-muted;
    text-decoration: line-through;
    cursor: not-allowed;
    opacity: 0.7;
  }
}
.csw-trust { font-size: 11px; color: $csw-muted; text-align: center; margin: 6px 0 0; }

.csw-footer {
  border-top: 1px solid $csw-line;
  padding: 14px 22px;
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; background: #fff;
  @media (max-width: 767px) {
    position: sticky; bottom: 0;
    padding-bottom: calc(14px + env(safe-area-inset-bottom));
  }
  .csw-btn--primary { margin-left: auto; }
}

.csw-btn {
  appearance: none; border: 0; border-radius: 8px;
  font-weight: 700; font-size: 14px;
  padding: 10px 18px; cursor: pointer; min-height: 44px;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid $csw-brand; outline-offset: 2px; }
}
.csw-btn--ghost {
  background: transparent; color: $csw-text; border: 1px solid $csw-line;
  &:hover:not(:disabled) { background: #f4f6f9; }
}
.csw-btn--primary { background: $csw-brand; color: #fff;
  &:hover:not(:disabled) { background: $csw-brand-dark; }
}
.csw-btn--pay { font-size: 15px; padding: 12px 22px; }
.csw-btn--full { width: 100%; margin: 12px 0 0; }
</style>
